import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DataDashboard from '../../../common/DataDashboard';
import HeaderActions from '../../../common/HeaderActions';
import { createStandardHeaderActions } from '../../../common/HeaderActions/helpers';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import { getDynamicSubItems } from '../../../../utils/getDynamicSubItems';
import { getRFQNavigationInfo, getRFQPageRoute, getRFQPageName } from '../../../../utils/rfqNavigation';
import { CATEGORIES, getCategoryName, getCategoriesForSelect } from '../../../../utils/categories';
import { useRfqLock } from '../../../../hooks/useRfqLock';
import RfqLockedModal from '../../../common/RfqLockedModal';
import CompanySelector from '../../../common/CompanySelector';
import ConfirmationModal from '../../../ConfirmationModal';
import SuccessModal from '../../../SuccessModal';
import RejectRequestModal from '../../modals/RejectRequestModal';
import './RFQManualProductsDashboard.scss';

interface UserRFQManualProductsDashboardProps {
  user: any;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
  manualMode?: 'rfq' | 'global';
}

interface ManualRequestProduct {
  _id: string;
  request_id: string;
  product_id?: string | null;
  product_name: string;
  product_brand?: string;
  category?: string | string[];
  description?: string;
  quantity: number;
  unit?: string;
  base_unit?: string;
  budget?: number;
  currency?: string;
  region?: string;
  is_manual?: boolean;
  notes?: string;
  color?: string;
  dimensions?: string;
  line_number?: number;
  created_at?: string;
  updated_at?: string;
  request_title?: string;
  request_company_request_id?: number;
  request_mail_id?: number;
  request_company_id?: string;
  company_name?: string;
  company_id_display?: string;
}

interface SimilarProduct {
  _id: string;
  product_id: string;
  product_name: string;
  brand?: string;
  category?: string | string[];
  base_unit?: string;
  dimensions?: string;
  color?: string;
  region?: string;
  similarity: number;
}

const UserRFQManualProductsDashboard: React.FC<UserRFQManualProductsDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout,
  manualMode = 'rfq'
}) => {
  const location = useLocation();
  const { token } = useAuth();

  // Check if user's company is on basic plan
  const isBasicPlan = user?.company?.plan === 'basic';

  const requestIdFromPath = useMemo(() => {
    const pathParts = location.pathname.split('/');

    // Handle /dashboard/user/rfq/:id pattern
    const rfqIndex = pathParts.indexOf('rfq');
    if (rfqIndex !== -1 && pathParts.length > rfqIndex + 1) {
      return pathParts[rfqIndex + 1];
    }

    // Handle /dashboard/rfqs/open/:id pattern
    const openIndex = pathParts.indexOf('open');
    if (openIndex !== -1 && pathParts.length > openIndex + 1) {
      return pathParts[openIndex + 1];
    }

    return null;
  }, [location.pathname]);

  const isGlobalMode = manualMode === 'global' || !requestIdFromPath;
  const requestId = isGlobalMode ? null : requestIdFromPath;

  // User version always uses /dashboard/user/rfq route
  const baseRoute = '/dashboard/user/rfq';

  const manualProductIdFromPath = useMemo(() => {
    const match = location.pathname.match(/manual-products\/([^/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const baseManualRoute = useMemo(() => {
    if (isGlobalMode) {
      return '/dashboard/products/manual-products';
    }
    return requestId ? `${baseRoute}/${requestId}/manual-products` : null;
  }, [isGlobalMode, requestId, baseRoute]);

  const [manualProducts, setManualProducts] = useState<ManualRequestProduct[]>([]);
  const [rfqDetail, setRfqDetail] = useState<any>(null); // New State for RFQ Info
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false); // Modal state
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false); // Success Modal state
  const [successMessage, setSuccessMessage] = useState(''); // Success Message
  const [isNavigationBlockModalOpen, setIsNavigationBlockModalOpen] = useState(false); // Navigation block modal
  const [pendingNavigationRoute, setPendingNavigationRoute] = useState<string | null>(null); // Route to navigate after confirmation

  // Company-specific codes state
  const [selectedCompanies, setSelectedCompanies] = useState<Array<{ company_id: string; company_name: string }>>([]);
  const [companyCodes, setCompanyCodes] = useState<Record<string, string>>({});

  const [approveTarget, setApproveTarget] = useState<ManualRequestProduct | null>(null);
  const [editForm, setEditForm] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);

  const [similarProducts, setSimilarProducts] = useState<SimilarProduct[]>([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarError, setSimilarError] = useState<string | null>(null);
  const [selectedSimilarId, setSelectedSimilarId] = useState<string | null>(null);
  const defaultSimilarLimit = 200;
  const [similarFetchParams, setSimilarFetchParams] = useState<{ search?: string; limit?: number }>({
    limit: defaultSimilarLimit
  });
  const [assigning, setAssigning] = useState(false);
  const [dashboardKey, setDashboardKey] = useState(0);
  const [creatingProduct, setCreatingProduct] = useState(false);

  // Approval flow states
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);


  const [showRejectModal, setShowRejectModal] = useState(false);

  // Control Phase states
  const [isConfirmingControl, setIsConfirmingControl] = useState(false);
  const [isRejectingControl, setIsRejectingControl] = useState(false);
  const [showRejectControlModal, setShowRejectControlModal] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);

  // RFQ Lock Hook
  const { lockInfo, showLockModal, setShowLockModal } = useRfqLock(requestId);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);


  // Edit request states (for rejected RFQs)
  const [isEditingRequest, setIsEditingRequest] = useState(false);


  const [isSavingRequest, setIsSavingRequest] = useState(false);
  const [requestEditForm, setRequestEditForm] = useState<{
    title: string;
    contact_person: string;
    delivery_address: string;
    invoice_address: string;
  }>({
    title: '',
    contact_person: '',
    delivery_address: '',
    invoice_address: ''
  });

  // Address dropdown states (for edit mode)
  const [addresses, setAddresses] = useState<any[]>([]);
  const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] = useState<string>('');
  const [selectedInvoiceAddressId, setSelectedInvoiceAddressId] = useState<string>('');

  // Product editing states (for rejected RFQs)
  const [editingProduct, setEditingProduct] = useState<ManualRequestProduct | null>(null);
  const [showProductEditModal, setShowProductEditModal] = useState(false);
  const [isUpdatingProduct, setIsUpdatingProduct] = useState(false);
  const [productEditForm, setProductEditForm] = useState<{
    product_name: string;
    product_brand: string;
    category: string;
    quantity: number;
    unit: string;
    budget: number;
    currency: string;
  }>({
    product_name: '',
    product_brand: '',
    category: '',
    quantity: 1,
    unit: 'adet',
    budget: 0,
    currency: 'TRY'
  });

  // Inline edits state - tracks pending changes per product ID
  const [inlineEdits, setInlineEdits] = useState<Record<string, Partial<ManualRequestProduct>>>({});

  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isGordianAdmin = user?.role === 'useradmin'; // Gordian Admin (Super Admin context)

  // Permissions
  // Fallback to true for admins if permissions are missing (backward compatibility)

  const canControl = user?.permissions?.control_ability ?? (user?.role === 'useradmin' || user?.role === 'admin' || user?.is_admin); // Admin roles have control ability by default

  const userCompanyId = user?.company_id;
  const userCompanyName = user?.company_name || 'Your Company';

  // Auto-select user's company if they're a regular user
  useEffect(() => {
    if (!isAdmin && userCompanyId && approveTarget) {
      setSelectedCompanies([{ company_id: userCompanyId, company_name: userCompanyName }]);
      setCompanyCodes({ [userCompanyId]: '1' }); // Default code "1"
    }
  }, [isAdmin, userCompanyId, userCompanyName, approveTarget]);

  // Derived state: check if any products are manual (unapproved)
  const hasManualProducts = useMemo(() => {
    return manualProducts.some(p => p.is_manual === true);
  }, [manualProducts]);

  const manualProductCount = useMemo(() => {
    return manualProducts.filter(p => p.is_manual === true).length;
  }, [manualProducts]);

  // Determine if current user can approve RFQs
  const canApprove = useMemo(() => {
    if (!user) return false;
    console.log('🔍 canApprove check (RFQ Review):', {
      user_id: user._id || user.id,
      role: user.role,
      is_admin: user.is_admin,
      permissions: user.permissions,
      approve_ability_nested: user.permissions?.approve_ability
    });
    if (user.role === 'admin' || user.is_admin) return true;
    if (user.permissions?.approve_ability === true) return true;
    return false;
  }, [user]);

  // Determine if user is RFQ owner
  const isOwner = useMemo(() => {
    if (!user || !rfqDetail) return false;
    return user._id === rfqDetail.user_id || user.id === rfqDetail.user_id;
  }, [user, rfqDetail]);

  // Approval Handlers
  // Approval Handlers
  const handleApproveRequest = () => {
    if (!requestId || !token || !rfqDetail) return;
    setIsApproveModalOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!requestId || !token || !rfqDetail) return;

    setIsApproving(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_admin_approval: true
        })
      });

      const data = await response.json();
      if (data.success) {
        setIsApproveModalOpen(false);
        setSuccessMessage('Request approved successfully');
        setIsSuccessModalOpen(true);
        setRfqDetail({
          ...rfqDetail,
          company_admin_approval: true,
          company_approval_status: 'approved_by_company',
          control_status: 'waiting'
        });
        // Refresh to update UI state properly
        fetchManualProducts();
      } else {
        throw new Error(data.message || 'Failed to approve request');
      }
    } catch (error: any) {
      console.error('Error approving request:', error);
      alert('Failed to approve request: ' + error.message);
    } finally {
      setIsApproving(false);
      setIsApproveModalOpen(false);
    }
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!requestId || !token) return;

    setIsRejecting(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reject: true,
          reject_reason: reason
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Request rejected successfully');
        setRfqDetail({ ...rfqDetail, company_admin_rejection: true, company_admin_approval: false });
        setShowRejectModal(false);
      } else {
        throw new Error(data.message || 'Failed to reject request');
      }
    } catch (error: any) {
      console.error('Error rejecting request:', error);
      alert('Failed to reject request: ' + error.message);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleResubmit = async () => {
    if (!requestId || !token) return;
    if (!window.confirm('Are you sure you want to resubmit this request for approval?')) return;

    setIsResubmitting(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/resubmit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        alert('Request resubmitted successfully');
        window.location.reload();
      } else {
        throw new Error(data.message || 'Failed to resubmit request');
      }
    } catch (error: any) {
      console.error('Error resubmitting request:', error);
      alert('Failed to resubmit request: ' + error.message);
    } finally {
      setIsResubmitting(false);
    }
  };

  const handleResubmitControl = async () => {
    if (!requestId || !token) return;
    if (!window.confirm('Are you sure you want to resubmit this request for control approval?')) return;

    setIsResubmitting(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/useradmin-control-resubmit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        alert('Request resubmitted for control successfully');
        window.location.reload();
      } else {
        throw new Error(data.message || 'Failed to resubmit request');
      }
    } catch (error: any) {
      console.error('Error resubmitting request:', error);
      alert('Failed to resubmit request: ' + error.message);
    } finally {
      setIsResubmitting(false);
    }
  };

  const handleCancelRequest = () => {
    setIsCancelModalOpen(true);
  };

  const executeCancelRequest = async () => {
    if (!requestId || !token) return;

    setIsCancelling(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setIsCancelModalOpen(false);
        setSuccessMessage('Request cancelled successfully');
        setIsSuccessModalOpen(true);
        // Reload after success modal closes or delay
        setTimeout(() => window.location.reload(), 1500);
      } else {
        throw new Error(data.message || 'Failed to cancel request');
      }
    } catch (error: any) {
      console.error('Error cancelling request:', error);
      alert('Failed to cancel request: ' + error.message);
    } finally {
      setIsCancelling(false);
    }
  };

  // Initialize request edit form when rfqDetail loads
  useEffect(() => {
    if (rfqDetail) {
      setRequestEditForm({
        title: rfqDetail.title || '',
        contact_person: rfqDetail.contact_person || '',
        delivery_address: rfqDetail.delivery_address || '',
        invoice_address: rfqDetail.invoice_address || ''
      });
    }
  }, [rfqDetail]);

  // Fetch addresses when edit mode is enabled
  useEffect(() => {
    const fetchAddresses = async () => {
      if (!isEditingRequest || !user?.company_id) return;

      try {
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/addresses/companies/${user.company_id}/addresses`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAddresses(data.data || []);

          // Set default addresses if available
          const defaultAddress = data.data?.find((addr: any) => addr.is_default);
          if (defaultAddress) {
            setSelectedDeliveryAddressId(defaultAddress._id);
            setSelectedInvoiceAddressId(defaultAddress._id);
          }
        }
      } catch (error) {
        console.error('Error fetching addresses:', error);
      }
    };

    fetchAddresses();
  }, [isEditingRequest, user?.company_id, token]);

  // Handle entering edit mode for rejected RFQs
  const handleEditRequest = () => {
    if (rfqDetail) {
      setRequestEditForm({
        title: rfqDetail.title || '',
        contact_person: rfqDetail.contact_person || '',
        delivery_address: rfqDetail.delivery_address || '',
        invoice_address: rfqDetail.invoice_address || ''
      });
      // Reset address selections
      setSelectedDeliveryAddressId('');
      setSelectedInvoiceAddressId('');
      setIsEditingRequest(true);
    }
  };

  // Handle saving request changes
  const handleSaveRequestChanges = async () => {
    if (!requestId || !token) return;

    setIsSavingRequest(true);
    try {
      // First, save any pending inline product edits
      const pendingEditEntries = Object.entries(inlineEdits);
      for (const [productId, edits] of pendingEditEntries) {
        const product = manualProducts.find(p => p._id === productId);
        if (!product || !edits) continue;

        for (const [field, value] of Object.entries(edits)) {
          let apiPayload: any = { [field]: value };

          // Map unit to base_unit for backend
          if (field === 'unit') {
            apiPayload = { base_unit: value };
          }

          // For category, convert to array
          if (field === 'category' && typeof value === 'string') {
            apiPayload = { category: [value] };
          }

          await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/products/${productId}`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(apiPayload)
          });
        }
      }

      // Clear inline edits after saving
      setInlineEdits({});

      // Format addresses from dropdown selections if available
      let deliveryAddress = requestEditForm.delivery_address;
      let invoiceAddress = requestEditForm.invoice_address;

      if (selectedDeliveryAddressId) {
        const deliveryAddr = addresses.find(a => a._id === selectedDeliveryAddressId);
        if (deliveryAddr) {
          deliveryAddress = `${deliveryAddr?.street || ''}, ${deliveryAddr?.city || ''}, ${deliveryAddr?.state || ''} ${deliveryAddr?.postal_code || ''}, ${deliveryAddr?.country || ''}`;
        }
      }

      if (selectedInvoiceAddressId) {
        const invoiceAddr = addresses.find(a => a._id === selectedInvoiceAddressId);
        if (invoiceAddr) {
          invoiceAddress = `${invoiceAddr?.street || ''}, ${invoiceAddr?.city || ''}, ${invoiceAddr?.state || ''} ${invoiceAddr?.postal_code || ''}, ${invoiceAddr?.country || ''}`;
        }
      }

      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: requestEditForm.title,
          contact_person: requestEditForm.contact_person,
          delivery_address: deliveryAddress,
          invoice_address: invoiceAddress
        })
      });

      const data = await response.json();
      if (data.success) {
        // Update local state
        setRfqDetail({
          ...rfqDetail,
          title: requestEditForm.title,
          contact_person: requestEditForm.contact_person,
          delivery_address: deliveryAddress,
          invoice_address: invoiceAddress
        });
        setIsEditingRequest(false);
        alert('Request updated successfully');
      } else {
        throw new Error(data.message || 'Failed to update request');
      }
    } catch (error: any) {
      console.error('Error saving request:', error);
      alert('Failed to save request: ' + error.message);
    } finally {
      setIsSavingRequest(false);
    }
  };

  // Handle opening product edit modal
  const handleOpenProductEdit = (product: ManualRequestProduct) => {
    setEditingProduct(product);
    setProductEditForm({
      product_name: product.product_name || '',
      product_brand: product.product_brand || '',
      category: Array.isArray(product.category) ? product.category.join(', ') : (product.category || ''),
      quantity: product.quantity || 1,
      unit: product.unit || 'adet',
      budget: product.budget || 0,
      currency: product.currency || 'TRY'
    });
    setShowProductEditModal(true);
  };

  // Handle saving product edit
  const handleSaveProductEdit = async () => {
    if (!editingProduct || !requestId || !token) return;

    setIsUpdatingProduct(true);
    try {
      const payload: any = {
        quantity: productEditForm.quantity,
        unit: productEditForm.unit
      };

      // Only include these fields for manual products
      if (editingProduct.is_manual) {
        payload.product_name = productEditForm.product_name;
        payload.product_brand = productEditForm.product_brand;
        payload.category = productEditForm.category.split(',').map(c => c.trim()).filter(Boolean);
        payload.budget = productEditForm.budget;
        payload.currency = productEditForm.currency;
      }

      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/products/${editingProduct._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        // Update local state
        setManualProducts(prev =>
          prev.map(p => p._id === editingProduct._id ? { ...p, ...payload } : p)
        );
        setShowProductEditModal(false);
        setEditingProduct(null);
        alert('Product updated successfully');
      } else {
        throw new Error(data.message || 'Failed to update product');
      }
    } catch (error: any) {
      console.error('Error updating product:', error);
      alert('Failed to update product: ' + error.message);
    } finally {
      setIsUpdatingProduct(false);
    }
  };

  // Handle deleting product from request
  const handleDeleteProduct = async (product: ManualRequestProduct) => {
    if (!requestId || !token) return;
    if (!window.confirm(`Are you sure you want to remove "${product.product_name}" from this request?`)) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/products/${product._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        // Remove from local state
        setManualProducts(prev => prev.filter(p => p._id !== product._id));
        alert('Product removed successfully');
      } else {
        throw new Error(data.message || 'Failed to remove product');
      }
    } catch (error: any) {
      console.error('Error removing product:', error);
      alert('Failed to remove product: ' + error.message);
    }
  };

  // Handle inline field change (track in inlineEdits state)
  const handleInlineChange = (productId: string, field: string, value: any) => {
    setInlineEdits(prev => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        [field]: value
      }
    }));
  };

  // Save inline edit on blur
  const handleSaveInlineEdit = async (product: ManualRequestProduct, field: string) => {
    if (!requestId || !token) return;

    const edits = inlineEdits[product._id];
    if (!edits || edits[field as keyof typeof edits] === undefined) return;

    const newValue = edits[field as keyof typeof edits];

    // Don't save if value hasn't changed
    const originalValue = product[field as keyof ManualRequestProduct];
    if (newValue === originalValue) {
      // Clear pending edit
      setInlineEdits(prev => {
        const updated = { ...prev };
        if (updated[product._id]) {
          delete (updated[product._id] as any)[field];
          if (Object.keys(updated[product._id]).length === 0) {
            delete updated[product._id];
          }
        }
        return updated;
      });
      return;
    }

    try {
      const payload: any = { [field]: newValue };
      let apiPayload: any = { [field]: newValue };

      // Map unit to base_unit for backend
      if (field === 'unit') {
        apiPayload = { base_unit: newValue };
      }

      // For category, convert to array
      if (field === 'category' && typeof newValue === 'string') {
        payload.category = [newValue];
        apiPayload.category = [newValue];
      }

      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/products/${product._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiPayload)
      });

      const data = await response.json();
      if (data.success) {
        // Update local state
        setManualProducts(prev =>
          prev.map(p => p._id === product._id ? { ...p, ...payload } : p)
        );
        // Clear pending edit
        setInlineEdits(prev => {
          const updated = { ...prev };
          if (updated[product._id]) {
            delete (updated[product._id] as any)[field];
            if (Object.keys(updated[product._id]).length === 0) {
              delete updated[product._id];
            }
          }
          return updated;
        });
      } else {
        console.error('Failed to update:', data.message);
      }
    } catch (error: any) {
      console.error('Error saving inline edit:', error);
    }
  };

  // Navigation blocking: prevent navigating to next RFQ steps if manual products exist
  // Only blocks: Search Suppliers, Communication, Quotes Review
  const blockedRoutePatterns = ['/suppliers', '/communication', '/quotes-review'];

  const handleBlockedNavigate = useCallback((route: string) => {
    // Check if navigating to a blocked RFQ flow page
    // Block conditions:
    // 1. Has manual products awaiting approval
    // 2. Is in control phase (waiting for control confirmation)
    // 3. Company approval is pending (not yet approved by company admin)
    const isControlPhase = rfqDetail?.company_approval_status === 'approved_by_company' && rfqDetail?.control_status === 'waiting';
    const isPendingApproval = rfqDetail && rfqDetail.company_admin_approval === false && !rfqDetail.company_admin_rejection;
    const isBlockedCondition = (hasManualProducts || isControlPhase || isPendingApproval) && !isGordianAdmin;

    const isBlockedRoute = isBlockedCondition && blockedRoutePatterns.some(pattern => route.includes(pattern));

    if (isBlockedRoute) {
      // Show modal - user must approve/complete current step first
      setPendingNavigationRoute(route);
      setIsNavigationBlockModalOpen(true);
      return; // Block navigation
    }
    // Allow navigation to other pages
    onNavigate(route);
  }, [hasManualProducts, rfqDetail, isGordianAdmin, onNavigate]);

  // Handle navigation confirmation from modal
  const handleConfirmNavigation = useCallback(() => {
    setIsNavigationBlockModalOpen(false);
    if (pendingNavigationRoute) {
      onNavigate(pendingNavigationRoute);
      setPendingNavigationRoute(null);
    }
  }, [pendingNavigationRoute, onNavigate]);

  const handleCancelNavigation = useCallback(() => {
    setIsNavigationBlockModalOpen(false);
    setPendingNavigationRoute(null);
  }, []);

  const fetchManualProducts = useCallback(async () => {
    if (!token) return;
    if (!isGlobalMode && !requestId) return;

    setIsLoading(true);
    setError(null);

    try {
      const endpoint = isGlobalMode
        ? `${API_ENDPOINTS.REQUEST_V2_MANUAL_PRODUCTS_ALL}?limit=200&sort_order=desc`
        : API_ENDPOINTS.REQUEST_V2_PRODUCTS(requestId!);

      // Fetch products and RFQ detail in parallel if we have a requestId
      const promises: Promise<Response>[] = [
        fetch(endpoint, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ];

      if (requestId && !isGlobalMode) {
        promises.push(
          fetch(API_ENDPOINTS.REQUEST_V2_DETAIL(requestId), {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          })
        );
      }

      const responses = await Promise.all(promises);
      const [productsResponse, rfqResponse] = responses;

      if (!productsResponse.ok) {
        throw new Error(`HTTP error! status: ${productsResponse.status}`);
      }

      const data = await productsResponse.json();
      if (data.success && Array.isArray(data.data)) {
        setManualProducts(data.data);
      } else {
        throw new Error(data.message || 'Failed to load products');
      }

      // Handle RFQ detail response
      if (rfqResponse && rfqResponse.ok) {
        const rfqData = await rfqResponse.json();
        if (rfqData.success) {
          setRfqDetail(rfqData.data);
        }
      }
    } catch (err: any) {
      console.error('❌ Error fetching RFQ data:', err);
      setError(err.message || 'Failed to load RFQ data');
    } finally {
      setIsLoading(false);
    }
  }, [isGlobalMode, requestId, token]);

  useEffect(() => {
    fetchManualProducts();
  }, [fetchManualProducts]);

  // Redirect if request is in select_supplier phase (finalized)
  useEffect(() => {
    if (rfqDetail && rfqDetail.phase === 'select_supplier') {
      // This RFQ is finalized, user shouldn't access detail pages anymore
      onNavigate('/dashboard/user/inbox/open');
    }
  }, [rfqDetail, onNavigate]);

  const fetchSimilarProducts = useCallback(async (
    targetProductId: string,
    targetRequestId?: string | null,
    options: { search?: string; limit?: number } = {}
  ) => {
    const resolvedRequestId = targetRequestId || requestId;
    if (!resolvedRequestId || !token) return;

    setSimilarLoading(true);
    setSimilarError(null);

    try {
      const baseUrl = API_ENDPOINTS.REQUEST_PRODUCT_SIMILAR(resolvedRequestId, targetProductId);
      const url = new URL(baseUrl);
      const limitParam = options.limit ?? similarFetchParams.limit ?? defaultSimilarLimit;
      if (limitParam) {
        url.searchParams.set('limit', String(limitParam));
      }
      if (options.search && options.search.trim().length > 0) {
        url.searchParams.set('search', options.search.trim());
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setSimilarProducts(data.data);
        if (data.data.length > 0) {
          const firstId = data.data[0]._id || data.data[0].product_id;
          setSelectedSimilarId(firstId ? String(firstId) : null);
        } else {
          setSelectedSimilarId(null);
        }
      } else {
        setSimilarProducts([]);
        setSelectedSimilarId(null);
      }
    } catch (err: any) {
      console.error('❌ Error fetching similar products:', err);
      setSimilarError(err.message || 'Failed to load similar products');
      setSimilarProducts([]);
      setSelectedSimilarId(null);
    } finally {
      setSimilarLoading(false);
    }
  }, [defaultSimilarLimit, requestId, similarFetchParams.limit, token]);

  const manualColumns = useMemo(() => {
    const canEditInline = isEditingRequest && (rfqDetail?.company_admin_rejection || rfqDetail?.control_status === 'rejected') && isOwner;
    const categoryOptions = getCategoriesForSelect('tr');

    // Exact unit options from ManualProductDashboard
    const unitOptions = [
      { value: 'adet', label: 'Adet (Piece)' },
      { value: 'kg', label: 'Kilogram (kg)' },
      { value: 'gr', label: 'Gram (gr)' },
      { value: 'lt', label: 'Liter (lt)' },
      { value: 'ml', label: 'Milliliter (ml)' },
      { value: 'metre', label: 'Meter (m)' },
      { value: 'mm', label: 'Millimeter (mm)' }
    ];

    return [
      // Remove button column - FIRST position for visibility
      ...(canEditInline ? [{
        key: 'remove',
        title: '',
        type: 'text' as const,
        sortable: false,
        width: '60px',
        editable: false,
        render: (_value: any, row: ManualRequestProduct) => (
          <button
            onClick={(e) => { e.stopPropagation(); handleDeleteProduct(row); }}
            style={{
              padding: '4px 8px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px'
            }}
          >
            ✕
          </button>
        )
      }] : []),
      {
        key: 'line_number',
        title: '#',
        type: 'number' as const,
        sortable: true,
        width: '50px',
        editable: false
      },
      {
        key: 'is_manual',
        title: 'Manual',
        type: 'text' as const,
        sortable: true,
        width: '80px',
        editable: false,
        render: (value: boolean) => (
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            backgroundColor: value ? '#dbeafe' : 'transparent',
            color: value ? '#1e40af' : 'inherit',
            fontWeight: value ? '500' : 'normal'
          }}>
            {value ? 'Yes' : 'No'}
          </span>
        )
      },
      {
        key: 'product_name',
        title: 'Product Name',
        type: 'text' as const,
        sortable: true,
        width: '220px',
        editable: false,
        render: (value: string) => value || '-'
      },
      {
        key: 'product_brand',
        title: 'Brand',
        type: 'text' as const,
        sortable: true,
        width: '140px',
        editable: false,
        render: (value: string) => value || '-'
      },
      {
        key: 'category',
        title: 'Category',
        type: 'text' as const,
        sortable: true,
        width: '220px',
        editable: false,
        render: (value: string | string[]) => {
          // Get category value (handle array or string)
          const categoryId = Array.isArray(value) ? value[0] : value;
          return categoryId ? getCategoryName(categoryId, 'tr') : '-';
        }
      },
      {
        key: 'quantity',
        title: 'Qty',
        type: 'number' as const,
        sortable: true,
        width: '70px',
        editable: false,
        render: (value: number, row: ManualRequestProduct) => {
          if (canEditInline) {
            const editValue = inlineEdits[row._id]?.quantity ?? value;
            return (
              <input
                type="number"
                value={editValue || 1}
                min="1"
                onChange={(e) => handleInlineChange(row._id, 'quantity', parseInt(e.target.value) || 1)}
                onBlur={() => handleSaveInlineEdit(row, 'quantity')}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '4px 8px',
                  border: '1px solid #3b82f6',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}
              />
            );
          }
          return value || 1;
        }
      },
      {
        key: 'unit',
        title: 'Unit',
        type: 'text' as const,
        sortable: true,
        width: '110px',
        editable: false,
        render: (value: string) => {
          // Display unit value
          const unitLabel = unitOptions.find(u => u.value === value)?.label || value || 'adet';
          return unitLabel;
        }
      },
      // Budget column - only show for non-basic plan users
      ...(!isBasicPlan ? [{
        key: 'budget',
        title: 'Budget',
        type: 'number' as const,
        sortable: true,
        width: '100px',
        editable: false,
        render: (value: number, row: ManualRequestProduct) => {
          if (canEditInline && row.is_manual) {
            const editBudget = inlineEdits[row._id]?.budget ?? value;
            const editCurrency = inlineEdits[row._id]?.currency ?? row.currency ?? 'TRY';
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                  type="number"
                  value={editBudget || ''}
                  min="0"
                  onChange={(e) => handleInlineChange(row._id, 'budget', parseFloat(e.target.value) || 0)}
                  onBlur={() => handleSaveInlineEdit(row, 'budget')}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '60px',
                    padding: '4px 6px',
                    border: '1px solid #3b82f6',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}
                />
                <select
                  value={editCurrency}
                  onChange={(e) => {
                    handleInlineChange(row._id, 'currency', e.target.value);
                    setTimeout(() => handleSaveInlineEdit(row, 'currency'), 100);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    padding: '4px 4px',
                    border: '1px solid #3b82f6',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: '#fff'
                  }}
                >
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            );
          }
          if (!value && value !== 0) return '-';
          const currency = row.currency || 'TRY';
          return `${value} ${currency}`;
        }
      }] : []),
      {
        key: 'region',
        title: 'Region',
        type: 'text' as const,
        sortable: true,
        width: '80px',
        editable: false,
        render: (value: string) => (value ? value.toUpperCase() : '-')
      },
      {
        key: 'created_at',
        title: 'Created At',
        type: 'date' as const,
        sortable: true,
        width: '140px',
        editable: false
      }
    ];
  }, [isEditingRequest, rfqDetail?.company_admin_rejection, isOwner, inlineEdits]);

  const globalColumns = useMemo(() => [
    {
      key: 'request_company_request_id',
      title: 'Company Req #',
      type: 'text' as const,
      sortable: true,
      width: '140px',
      editable: false,
      render: (value: number | undefined) => (value !== undefined ? value : '-')
    },
    {
      key: 'request_title',
      title: 'Request Title',
      type: 'text' as const,
      sortable: true,
      width: '220px',
      editable: false,
      render: (value: string | undefined) => value || '-'
    },
    {
      key: 'company_name',
      title: 'Company',
      type: 'text' as const,
      sortable: true,
      width: '200px',
      editable: false,
      render: (value: string | undefined, row: ManualRequestProduct) =>
        value || row.company_id_display || '-'
    }
  ], []);

  const activeColumns = useMemo(
    () => (isGlobalMode ? [...globalColumns, ...manualColumns] : manualColumns),
    [globalColumns, isGlobalMode, manualColumns]
  );

  const similarDashboardColumns = useMemo(
    () => [
      {
        key: 'product_id',
        title: 'Global ID',
        type: 'text' as const,
        sortable: true,
        width: '120px',
        editable: false
      },
      {
        key: 'name',
        title: 'Product Name',
        type: 'text' as const,
        sortable: true,
        width: '240px',
        editable: false
      },
      {
        key: 'brand',
        title: 'Brand',
        type: 'text' as const,
        sortable: true,
        width: '160px',
        editable: false
      },
      {
        key: 'category',
        title: 'Category',
        type: 'text' as const,
        sortable: true,
        width: '200px',
        editable: false,
        render: (value: string | string[]) => {
          if (!value) return '-';
          if (Array.isArray(value)) return value.join(', ');
          return value;
        }
      },
      {
        key: 'base_unit',
        title: 'Base Unit',
        type: 'text' as const,
        sortable: true,
        width: '110px',
        editable: false
      },
      {
        key: 'region',
        title: 'Region',
        type: 'text' as const,
        sortable: true,
        width: '90px',
        editable: false,
        render: (value: string) => (value ? value.toUpperCase() : '-')
      },
      {
        key: 'similarity',
        title: 'Similarity',
        type: 'number' as const,
        sortable: true,
        width: '120px',
        editable: false,
        render: (value: number) => `${value}%`
      }
    ],
    []
  );

  const similarDashboardData = useMemo(
    () =>
      similarProducts
        .map(product => {
          const rowId = product._id || product.product_id;
          if (!rowId) {
            return null;
          }

          const normalizedCategory = Array.isArray(product.category)
            ? product.category
            : product.category
              ? [product.category]
              : [];

          return {
            _id: String(rowId),
            product_id: product.product_id || '',
            name: product.product_name || '',
            brand: product.brand || '',
            category: normalizedCategory,
            base_unit: product.base_unit || '',
            region: product.region || '',
            similarity: Math.round((product.similarity || 0) * 100)
          };
        })
        .filter((row): row is {
          _id: string;
          product_id: string;
          name: string;
          brand: string;
          category: string[];
          base_unit: string;
          region: string;
          similarity: number;
        } => Boolean(row)),
    [similarProducts]
  );


  const handleSelectionChange = (ids: string[]) => {
    console.log('✅ manually selection changed:', ids);
    setSelectedIds(ids);
  };

  const resetSelection = () => {
    setSelectedIds([]);
    setDashboardKey(prev => prev + 1);
  };

  const showApprovalPanel = useCallback(
    (product: ManualRequestProduct, options: { navigate?: boolean } = {}) => {
      const { navigate = false } = options; // Default to false for unified dashboard

      setApproveTarget(product);
      setSelectedIds([product._id]);
      setEditForm({
        product_name: product.product_name || '',
        product_brand: product.product_brand || '',
        category: Array.isArray(product.category) ? product.category.join(', ') : (product.category || ''),
        description: product.description || '',
        quantity: product.quantity ?? 0,
        unit: product.unit || '',
        base_unit: product.base_unit || '',
        budget: product.budget ?? '',
        currency: product.currency || 'TRY',
        region: product.region || '',
        notes: product.notes || '',
        color: product.color || '',
        dimensions: product.dimensions || ''
      });
      setSimilarProducts([]);
      setSimilarError(null);
      setSelectedSimilarId(null);
      setSimilarFetchParams({ limit: defaultSimilarLimit });

      const productRequestId =
        typeof product.request_id === 'string'
          ? product.request_id
          : product.request_id
            ? String(product.request_id)
            : null;

      fetchSimilarProducts(product._id, productRequestId, { limit: defaultSimilarLimit });

      if (navigate && baseManualRoute) {
        const targetPath = `${baseManualRoute}/${product._id}`;
        if (location.pathname !== targetPath) {
          onNavigate(targetPath);
        }
      }
    },
    [baseManualRoute, fetchSimilarProducts, location.pathname, onNavigate]
  );

  const openApprovalPanel = (product: ManualRequestProduct) => {
    showApprovalPanel(product);
  };

  useEffect(() => {
    if (!manualProductIdFromPath) {
      return;
    }

    const targetProduct = manualProducts.find(product => product._id === manualProductIdFromPath);
    if (targetProduct && (!approveTarget || approveTarget._id !== targetProduct._id)) {
      showApprovalPanel(targetProduct, { navigate: false });
    }
  }, [approveTarget, manualProductIdFromPath, manualProducts, showApprovalPanel]);

  const handleApproveClick = () => {
    if (selectedIds.length !== 1) return;
    const target = manualProducts.find(product => product._id === selectedIds[0]);
    if (!target) return;
    openApprovalPanel(target);
  };

  const handleRefreshSimilar = () => {
    if (approveTarget) {
      const targetRequestId =
        typeof approveTarget.request_id === 'string'
          ? approveTarget.request_id
          : approveTarget.request_id
            ? String(approveTarget.request_id)
            : null;
      fetchSimilarProducts(approveTarget._id, targetRequestId, similarFetchParams);
    }
  };

  const handleSimilarSelectionChange = useCallback((ids: string[]) => {
    const lastSelected = ids.length > 0 ? ids[ids.length - 1] : null;
    setSelectedSimilarId(lastSelected);
  }, []);

  const handleSimilarDataFetch = useCallback((fetchConfig: { search?: string; limit?: number }) => {
    if (!approveTarget) return;
    const targetRequestId =
      typeof approveTarget.request_id === 'string'
        ? approveTarget.request_id
        : approveTarget.request_id
          ? String(approveTarget.request_id)
          : requestId;

    if (!targetRequestId) return;

    const hasSearch = Object.prototype.hasOwnProperty.call(fetchConfig, 'search');
    const normalizedSearch = fetchConfig?.search?.trim();
    const nextParams: { search?: string; limit?: number } = {
      limit: fetchConfig?.limit ?? similarFetchParams.limit ?? defaultSimilarLimit
    };
    if (hasSearch) {
      if (normalizedSearch) {
        nextParams.search = normalizedSearch;
      }
    } else if (similarFetchParams.search) {
      nextParams.search = similarFetchParams.search;
    }

    setSimilarFetchParams(nextParams);
    fetchSimilarProducts(approveTarget._id, targetRequestId, nextParams);
  }, [approveTarget, defaultSimilarLimit, fetchSimilarProducts, requestId, similarFetchParams]);

  const handleFieldChange = (field: string, value: any) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };



  const handleSplitRFQ = () => {
    if (selectedIds.length === 0) {
      alert('Please select at least one product to split');
      return;
    }

    if (selectedIds.length === manualProducts.length) {
      alert('You cannot split all products. At least one product must remain in the original RFQ.');
      return;
    }

    setIsSplitModalOpen(true);
  };

  const executeSplitRFQ = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/split`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_ids: selectedIds
        })
      });

      if (!response.ok) {
        throw new Error('Failed to split RFQ');
      }

      const data = await response.json();
      if (data.success) {
        setIsSplitModalOpen(false); // Close confirmation modal first
        setSuccessMessage(`RFQ split successfully! New RFQ ID: ${data.data.new_request_id}`);
        setIsSuccessModalOpen(true);
        // Reload to refresh the list (removing split products)
        fetchManualProducts();
        setSelectedIds([]);
      } else {
        throw new Error(data.message || 'Failed to split RFQ');
      }
    } catch (error: any) {
      console.error('Error splitting RFQ:', error);
      alert('Failed to split RFQ: ' + error.message);
      setIsSplitModalOpen(false); // Close modal on error too
    }
  };

  const handleSaveManualChanges = async () => {
    if (!approveTarget || !token) return;

    const targetRequestId =
      typeof approveTarget.request_id === 'string'
        ? approveTarget.request_id
        : approveTarget.request_id
          ? String(approveTarget.request_id)
          : requestId;

    if (!targetRequestId) {
      alert('Unable to determine request for this manual product.');
      return;
    }

    const payload: Record<string, any> = {};
    const fieldMappings: Array<[string, string]> = [
      ['product_name', 'product_name'],
      ['product_brand', 'product_brand'],
      ['description', 'description'],
      ['notes', 'notes'],
      ['unit', 'unit'],
      ['base_unit', 'base_unit'],
      ['region', 'region'],
      ['color', 'color'],
      ['dimensions', 'dimensions']
    ];

    fieldMappings.forEach(([formKey, payloadKey]) => {
      if (editForm[formKey] !== (approveTarget as any)[payloadKey]) {
        payload[payloadKey] = editForm[formKey];
      }
    });

    if (Number(editForm.quantity) !== approveTarget.quantity) {
      payload.quantity = Number(editForm.quantity) || 0;
    }

    if (editForm.budget !== approveTarget.budget) {
      const parsedBudget = editForm.budget === '' ? '' : parseFloat(editForm.budget);
      payload.budget = Number.isNaN(parsedBudget) ? 0 : parsedBudget;
    }

    if ((editForm.currency || '').toUpperCase() !== (approveTarget.currency || '').toUpperCase()) {
      payload.currency = (editForm.currency || 'TRY').toUpperCase();
    }

    if (editForm.category !== (Array.isArray(approveTarget.category) ? approveTarget.category.join(', ') : (approveTarget.category || ''))) {
      const categoryValue = editForm.category;
      if (typeof categoryValue === 'string') {
        payload.category = categoryValue
          .split(',')
          .map((item: string) => item.trim())
          .filter(Boolean);
      }
    }

    if (Object.keys(payload).length === 0) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${targetRequestId}/products/${approveTarget._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to update manual product');
      }

      const data = await response.json();
      if (data.success) {
        setManualProducts(prev =>
          prev.map(item => (item._id === approveTarget._id ? { ...item, ...payload } : item))
        );
        setApproveTarget(prev => (prev ? { ...prev, ...payload } : prev));
      } else {
        throw new Error(data.message || 'Failed to update manual product');
      }
    } catch (err: any) {
      console.error('❌ Error updating manual product:', err);
      alert(err.message || 'Failed to update manual product');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Confirm Control
  const handleConfirmControl = async () => {
    if (!requestId) return;

    try {
      setIsConfirmingControl(true);
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/useradmin-control-confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to confirm control phase');
      }

      setSuccessMessage('Request control phase confirmed successfully.');
      setIsSuccessModalOpen(true);
      fetchManualProducts(); // Refresh data
    } catch (error: any) {
      console.error('Error confirming control:', error);
      alert(error.message || 'Failed to confirm control');
    } finally {
      setIsConfirmingControl(false);
    }
  };

  // Handle Reject Control
  const handleRejectControl = async (reason: string) => {
    if (!requestId) return;

    try {
      setIsRejectingControl(true);
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/useradmin-control-reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rejection_reason: reason })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reject control phase');
      }

      setShowRejectControlModal(false);
      setSuccessMessage('Request control phase rejected successfully.');
      setIsSuccessModalOpen(true);
      fetchManualProducts(); // Refresh data
    } catch (error: any) {
      console.error('Error rejecting control:', error);
      alert(error.message || 'Failed to reject control');
    } finally {
      setIsRejectingControl(false);
    }
  };

  const handleAssignSimilar = async () => {
    if (!approveTarget || !token || !selectedSimilarId) return;

    const targetRequestId =
      typeof approveTarget.request_id === 'string'
        ? approveTarget.request_id
        : approveTarget.request_id
          ? String(approveTarget.request_id)
          : requestId;

    if (!targetRequestId) {
      alert('Unable to determine request for this manual product.');
      return;
    }

    setAssigning(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${targetRequestId}/products/${approveTarget._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: selectedSimilarId,
          is_manual: false
        })
      });

      if (!response.ok) {
        throw new Error('Failed to assign manual product to catalog product');
      }

      const data = await response.json();
      if (data.success) {
        alert('Manual product successfully linked to catalog product.');
        fetchManualProducts();
        handleExitApproval(`${baseRoute}/${requestId}/manual-products`);
      } else {
        throw new Error(data.message || 'Failed to assign manual product');
      }
    } catch (err: any) {
      console.error('❌ Error assigning manual product:', err);
      alert(err.message || 'Failed to assign manual product');
    } finally {
      setAssigning(false);
    }
  };

  const handleCreateCatalogProduct = async () => {
    if (!approveTarget || !token) {
      return;
    }

    const targetRequestId =
      typeof approveTarget.request_id === 'string'
        ? approveTarget.request_id
        : approveTarget.request_id
          ? String(approveTarget.request_id)
          : requestId;

    if (!targetRequestId) {
      alert('Unable to determine request for this manual product.');
      return;
    }

    const normalizedName = (editForm.product_name || approveTarget.product_name || '').trim();
    if (!normalizedName) {
      alert('Product name is required before creating a catalog product.');
      return;
    }

    const regionCandidate = (editForm.region || approveTarget.region || 'tr').toString().trim().toLowerCase();
    const normalizedRegion = ['tr', 'en', 'de'].includes(regionCandidate) ? regionCandidate : 'tr';

    const categoryString =
      typeof editForm.category === 'string'
        ? editForm.category
        : Array.isArray(approveTarget.category)
          ? approveTarget.category.join(', ')
          : approveTarget.category || '';

    const createPayload = {
      name: normalizedName,
      brand: (editForm.product_brand || approveTarget.product_brand || '').trim(),
      category: categoryString,
      description: (editForm.description || approveTarget.description || '').trim(),
      base_unit: (editForm.base_unit || approveTarget.base_unit || '').trim(),
      dimensions: (editForm.dimensions || approveTarget.dimensions || '').trim(),
      color: (editForm.color || approveTarget.color || '').trim(),
      region: normalizedRegion
    };

    setCreatingProduct(true);
    try {
      const createResponse = await fetch(API_ENDPOINTS.PRODUCTS_V2, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createPayload)
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create catalog product');
      }

      const createData = await createResponse.json();
      const createdProductObjectId = createData?.data?._id || createData?.id;

      if (!createdProductObjectId) {
        throw new Error('Catalog product created but ID is missing in response');
      }

      const linkResponse = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${targetRequestId}/products/${approveTarget._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: createdProductObjectId,
          is_manual: false,
          region: normalizedRegion
        })
      });

      if (!linkResponse.ok) {
        throw new Error('Failed to link new catalog product to manual product');
      }

      const linkData = await linkResponse.json();
      if (!linkData.success) {
        throw new Error(linkData.message || 'Failed to link new catalog product');
      }

      alert('Manual product promoted to catalog successfully.');
      fetchManualProducts();
      handleExitApproval(`${baseRoute}/${requestId}/manual-products`);
    } catch (err: any) {
      console.error('❌ Error creating catalog product from manual entry:', err);
      alert(err.message || 'Failed to create catalog product');
    } finally {
      setCreatingProduct(false);
    }
  };

  const handleExitApproval = (redirectRoute?: string) => {
    setApproveTarget(null);
    setSimilarProducts([]);
    setSimilarError(null);
    setSelectedSimilarId(null);
    setSimilarFetchParams({ limit: defaultSimilarLimit });
    setSimilarFetchParams({ limit: defaultSimilarLimit });
    // Unified dashboard: stay on current route
    resetSelection();
  };

  if (!requestId && !isGlobalMode) {
    return null;
  }

  // Standard header actions
  const headerActions = (
    <HeaderActions
      {...createStandardHeaderActions({
        isEditing: false,
        onEditToggle: () => { },
        onSaveChanges: () => { },
        onExport: () => { },
        onDelete: () => { },
        selectedItemsCount: selectedIds.length,
        itemsPerPage: 25,
        onItemsPerPageChange: () => { },
        onFiltersToggle: () => { },
        useIndividualComponents: true,
        showEditButton: false,
        showDeleteButton: false,
        showExportButton: false,
        showAddButton: false,
        customButtons: [
          // Approve Button (only if user can approve and request not yet approved/rejected)
          ...(rfqDetail && !rfqDetail.company_admin_approval && !rfqDetail.company_admin_rejection && canApprove ? [{
            label: isApproving ? 'Approving...' : 'Approve Request',
            onClick: handleApproveRequest,
            disabled: isApproving,
            type: 'primary' as const
          }] : []),
          // Reject Button (only if user can approve and request not yet approved/rejected)
          ...(rfqDetail && !rfqDetail.company_admin_approval && !rfqDetail.company_admin_rejection && canApprove ? [{
            label: 'Reject Request',
            onClick: () => setShowRejectModal(true),
            disabled: isRejecting,
            type: 'secondary' as const
          }] : []),
          // Finalize/Cancel Button (Approval Phase)
          ...(rfqDetail && rfqDetail.company_approval_status === 'waiting_for_approval' && canApprove ? [{
            label: 'Finalize (Cancel)',
            onClick: handleCancelRequest,
            disabled: isCancelling,
            type: 'secondary' as const
          }] : []),
          // Resubmit Button (only if owner and request is rejected via company_admin_rejection)
          ...(rfqDetail && rfqDetail.company_admin_rejection && isOwner ? [{
            label: isResubmitting ? 'Resubmitting...' : 'Resubmit for Approval',
            onClick: handleResubmit,
            disabled: isResubmitting,
            type: 'primary' as const
          }] : []),
          // Resubmit Control Button (only if owner and request is control rejected)
          ...(rfqDetail && rfqDetail.control_status === 'rejected' && isOwner ? [{
            label: isResubmitting ? 'Resubmitting...' : 'Resubmit for Control',
            onClick: handleResubmitControl,
            disabled: isResubmitting,
            type: 'primary' as const
          }] : []),
          // Edit Request Button (only if owner and request is rejected OR control rejected)
          ...(rfqDetail && (rfqDetail.company_admin_rejection || rfqDetail.control_status === 'rejected') && isOwner && !isEditingRequest ? [{
            label: 'Edit Request',
            onClick: handleEditRequest,
            disabled: false,
            type: 'secondary' as const
          }] : []),

          // Cancel Edit Button (only when editing)
          ...(isEditingRequest ? [{
            label: 'Cancel Edit',
            onClick: () => setIsEditingRequest(false),
            disabled: false,
            type: 'secondary' as const,
            componentProps: { isEditing: true }
          }] : []),
          // Cancel Request Button (red button, only if owner and request is rejected OR control rejected and NOT in edit mode)
          ...(rfqDetail && (rfqDetail.company_admin_rejection || rfqDetail.control_status === 'rejected') && isOwner && !isEditingRequest ? [{
            label: 'Cancel Request',
            onClick: handleCancelRequest,
            disabled: isCancelling,
            type: 'danger' as const
          }] : []),
          // Save Changes Button (only when editing)
          ...(isEditingRequest ? [{
            label: isSavingRequest ? 'Saving...' : 'Save Changes',
            onClick: handleSaveRequestChanges,
            disabled: isSavingRequest,
            type: 'primary' as const
          }] : []),
          // Control Phase Buttons
          // Confirm Control (only if approved, waiting control, NOT manual products, and has permission/admin)
          ...(rfqDetail && rfqDetail.company_approval_status === 'approved_by_company' && rfqDetail.control_status === 'waiting' && !hasManualProducts && (canControl || isGordianAdmin) ? [{
            label: isConfirmingControl ? 'Confirming...' : 'Confirm Request',
            onClick: handleConfirmControl,
            disabled: isConfirmingControl,
            type: 'primary' as const,
            icon: 'check'
          }] : []),

          // Reject Control (only if approved, waiting control, same permissions AND approve ability)
          ...(rfqDetail && rfqDetail.company_approval_status === 'approved_by_company' && rfqDetail.control_status === 'waiting' && (canControl || isGordianAdmin) && canApprove ? [{
            label: 'Reject Control',
            onClick: () => setShowRejectControlModal(true),
            disabled: isRejectingControl,
            type: 'secondary' as const
          }] : []),
          // Finalize/Cancel Button (Control Phase) - Visible to Control OR Approve ability
          ...(rfqDetail && rfqDetail.company_approval_status === 'approved_by_company' && rfqDetail.control_status === 'waiting' && (canControl || canApprove || isGordianAdmin) ? [{
            label: 'Finalize (Cancel)',
            onClick: handleCancelRequest,
            disabled: isCancelling,
            type: 'secondary' as const
          }] : []),

          // Add to Master Data (Only in Approve Phase OR Approved by Company but waiting control, Only Approve Ability)
          ...(rfqDetail && (rfqDetail.company_approval_status === 'waiting_for_approval' || rfqDetail.company_approval_status === 'approved_by_company') && hasManualProducts && (canApprove || isGordianAdmin) ? [{
            label: 'Add to Master Data',
            onClick: handleApproveClick,
            disabled: selectedIds.length !== 1 || !manualProducts.find(p => p._id === selectedIds[0])?.is_manual || !hasManualProducts,
            type: 'primary' as const,
            icon: 'check'
          }] : []),

          // Split (Control OR Approve Phase, Control OR Approve Ability)
          ...(rfqDetail && (rfqDetail.company_approval_status === 'waiting_for_approval' || (rfqDetail.company_approval_status === 'approved_by_company' && rfqDetail.control_status === 'waiting')) && (canControl || canApprove || isGordianAdmin) ? [{
            label: 'Split',
            onClick: handleSplitRFQ,
            disabled: selectedIds.length === 0,
            type: 'primary' as const
          }] : [])
        ]
      })}
    />
  );

  const approvalHeaderActions = approveTarget ? (
    <HeaderActions
      {...createStandardHeaderActions({
        isEditing: false,
        onEditToggle: () => { },
        onSaveChanges: () => { },
        onExport: () => { },
        onDelete: () => { },
        selectedItemsCount: 0,
        itemsPerPage: 25,
        onItemsPerPageChange: () => { },
        onFiltersToggle: () => { },
        useIndividualComponents: true,
        showEditButton: false,
        showDeleteButton: false,
        showExportButton: false,
        showAddButton: false,
        customButtons: [
          {
            label: 'Refresh Similar',
            onClick: handleRefreshSimilar,
            disabled: similarLoading,
            type: 'primary'
          },
          {
            label: 'Add to Master Data',
            onClick: handleCreateCatalogProduct,
            disabled: creatingProduct || assigning,
            type: 'primary'
          },
          {
            label: assigning ? 'Linking...' : 'Link to Selected',
            onClick: handleAssignSimilar,
            disabled: !selectedSimilarId || assigning || creatingProduct,
            type: 'primary'
          }
        ]
      })}
    />
  ) : null;

  // Navigation actions for approval mode - Back button like RFQ Review page
  const approvalNavigationActions = approveTarget ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <button
          onClick={() => handleExitApproval()}
          style={{
            padding: '8px 16px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            backgroundColor: '#ffffff',
            color: '#374151',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          ← Back to RFQ Review
        </button>
      </div>
    </div>
  ) : null;

  const approvalHeaderContent = approveTarget ? (
    <>
      <div className="manual-approval-panel">
        <div className="panel-header">
          <div>
            <h3>Manual Product Details</h3>
            <p>Review and adjust the manual entry before approving.</p>
          </div>
          <div className="panel-header-actions">
            {isGlobalMode && approveTarget?.request_id && (
              <button
                onClick={() =>
                  onNavigate(
                    `/dashboard/rfqs/open/${typeof approveTarget.request_id === 'string'
                      ? approveTarget.request_id
                      : String(approveTarget.request_id)
                    }`
                  )
                }
                className="secondary-button"
              >
                View RFQ
              </button>
            )}
          </div>
        </div>

        <div className="panel-body">
          {isGlobalMode && (
            <div className="manual-request-meta">
              <div>
                <span className="meta-label">Request Title:</span>
                <span className="meta-value">{approveTarget.request_title || '-'}</span>
              </div>
              <div>
                <span className="meta-label">Company:</span>
                <span className="meta-value">
                  {approveTarget.company_name || approveTarget.company_id_display || '-'}
                </span>
              </div>
              <div>
                <span className="meta-label">Req #:</span>
                <span className="meta-value">
                  {approveTarget.request_company_request_id ?? '-'}
                </span>
              </div>
            </div>
          )}

          <div className="manual-product-form">
            <div className="form-grid">
              <label>
                <span>Product Name <span style={{ color: '#ef4444' }}>*</span></span>
                <input
                  type="text"
                  value={editForm.product_name}
                  onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  placeholder="Enter product name"
                />
              </label>
              <label>
                <span>Brand <span style={{ color: '#ef4444' }}>*</span></span>
                <input
                  type="text"
                  value={editForm.product_brand}
                  onChange={(e) => handleFieldChange('product_brand', e.target.value)}
                  placeholder="Enter brand name"
                />
              </label>
              {isAdmin && (
                <label>
                  <span>Amazon ASIN</span>
                  <input
                    type="text"
                    value={editForm.amazon_asin || ''}
                    onChange={(e) => handleFieldChange('amazon_asin', e.target.value)}
                    placeholder="Enter Amazon ASIN"
                  />
                </label>
              )}
              <label>
                <span>Categories <span style={{ color: '#ef4444' }}>*</span></span>
                <select
                  value={editForm.category || ''}
                  onChange={(e) => handleFieldChange('category', e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">Select category...</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name.tr}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Base Unit <span style={{ color: '#ef4444' }}>*</span></span>
                <select
                  value={editForm.base_unit || 'kg'}
                  onChange={(e) => handleFieldChange('base_unit', e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="l">l</option>
                  <option value="ml">ml</option>
                  <option value="pcs">pcs</option>
                  <option value="box">box</option>
                  <option value="pack">pack</option>
                  <option value="m">m</option>
                  <option value="cm">cm</option>
                </select>
              </label>
              <label>
                <span>Size</span>
                <select
                  value={editForm.dimensions || ''}
                  onChange={(e) => handleFieldChange('dimensions', e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">Select Size (Optional)</option>
                  <option value="XS">XS</option>
                  <option value="S">S</option>
                  <option value="M">M</option>
                  <option value="L">L</option>
                  <option value="XL">XL</option>
                  <option value="XXL">XXL</option>
                </select>
              </label>
              <label>
                <span>Color</span>
                <input
                  type="text"
                  value={editForm.color}
                  onChange={(e) => handleFieldChange('color', e.target.value)}
                  placeholder="Enter color"
                />
              </label>
              <label>
                <span>Description</span>
                <input
                  type="text"
                  value={editForm.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Enter product description"
                />
              </label>
              {/* Company Code for users - in grid */}
              {!isAdmin && (
                <label>
                  <span>Company Code</span>
                  <input
                    type="text"
                    value={companyCodes[userCompanyId] || ''}
                    onChange={(e) => setCompanyCodes({
                      ...companyCodes,
                      [userCompanyId]: e.target.value
                    })}
                    placeholder="Enter code (default: 1)"
                  />
                </label>
              )}
            </div>

            {/* Company Specific Codes Section - Admin only */}
            {isAdmin && (
              <div style={{ marginTop: '0.5rem' }}>
                <span style={{ fontWeight: '600', color: '#1f2937', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                  Company Specific Codes (Optional)
                </span>
                <CompanySelector
                  value={selectedCompanies}
                  onChange={setSelectedCompanies}
                  placeholder="Select companies to add codes..."
                  multiple={true}
                />
              </div>
            )}

            {/* Company Codes Inputs - Admin only */}
            {isAdmin && selectedCompanies.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <span style={{ fontWeight: '600', color: '#1f2937', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                  Company Codes
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {selectedCompanies.map((company) => (
                    <div key={company.company_id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <label style={{ minWidth: '200px', fontSize: '13px', color: '#4b5563' }}>
                        {company.company_name}:
                      </label>
                      <input
                        type="text"
                        value={companyCodes[company.company_id] || ''}
                        onChange={(e) => setCompanyCodes({
                          ...companyCodes,
                          [company.company_id]: e.target.value
                        })}
                        style={{
                          flex: 1,
                          maxWidth: '200px',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '0.95rem'
                        }}
                        placeholder="Enter code (default: 1)"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="form-actions">
              <button
                className="primary-button"
                onClick={handleSaveManualChanges}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Link Product Section Header */}
      <div style={{
        padding: '1rem 1.25rem',
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '10px',
        marginTop: '1rem',
        boxShadow: '0 4px 12px -8px rgba(15, 23, 42, 0.35)'
      }}>
        <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', color: '#1f2937', fontWeight: '600' }}>
          Link Product
        </h3>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.85rem' }}>
          Select an existing product from the catalog below, or add this as a new product to master data.
        </p>
      </div>
    </>
  ) : null;

  // Warning card for manual products
  const manualProductWarningCard = hasManualProducts && rfqDetail?.company_approval_status === 'approved_by_company' ? (
    <div className="manual-products-warning-card" style={{
      padding: '1rem 1.5rem',
      backgroundColor: '#fffbeb',
      border: '2px solid #fbbf24',
      borderRadius: '8px',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    }}>
      <span style={{ fontSize: '1.5rem' }}>⚠️</span>
      <div>
        <p style={{ margin: 0, fontWeight: '600', color: '#92400e' }}>
          {manualProductCount} Manual Product{manualProductCount > 1 ? 's' : ''} Awaiting Approval
        </p>
        <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#a16207' }}>
          You must approve all manual products before proceeding. Select a manual product and click "Add to Master Data" to approve.
        </p>
      </div>
    </div>
  ) : null;

  // Warning card for Control Phase (when no manual products but waiting for control confirmation)
  const controlPhaseWarningCard = (!hasManualProducts && rfqDetail?.control_status === 'waiting' && rfqDetail?.company_approval_status === 'approved_by_company') ? (
    <div className="control-phase-warning-card" style={{
      padding: '1rem 1.5rem',
      backgroundColor: '#eff6ff', // Light blue background
      border: '2px solid #3b82f6', // Blue border
      borderRadius: '8px',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    }}>
      <span style={{ fontSize: '1.5rem' }}>ℹ️</span>
      <div>
        <p style={{ margin: 0, fontWeight: '600', color: '#1e40af' }}>
          Control Phase: Confirmation Required
        </p>
        <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#1e3a8a' }}>
          All manual products have been processed. Please review the request details and click "Confirm Request" to finalize.
        </p>
      </div>
    </div>
  ) : null;

  // Warning for Waiting Approval - if pending company admin approval (blue like control phase)
  const waitingApprovalWarningCard = (rfqDetail && rfqDetail.company_admin_approval === false && !rfqDetail.company_admin_rejection) ? (
    <div className="approval-warning-card" style={{
      padding: '1rem 1.5rem',
      backgroundColor: '#eff6ff', // Light blue background (like control phase)
      border: '2px solid #3b82f6', // Blue border
      borderRadius: '8px',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    }}>
      <span style={{ fontSize: '1.5rem' }}>ℹ️</span>
      <div>
        <p style={{ margin: 0, fontWeight: '600', color: '#1e40af' }}>
          Request Awaiting Approval
        </p>
        <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#1e3a8a' }}>
          This request is pending company admin approval. You cannot proceed to next steps until it is approved.
        </p>
      </div>
    </div>
  ) : null;

  // RFQ Info Card - editable when isEditingRequest is true
  const requestInfoCard = rfqDetail ? (
    <div className="rfq-info-section" style={{
      padding: '1.5rem',
      backgroundColor: isEditingRequest ? '#f0fdf4' : '#fff',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '1.5rem',
      border: isEditingRequest ? '2px solid #22c55e' : 'none'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
          Request Information {isEditingRequest && <span style={{ color: '#22c55e', fontSize: '14px' }}>(Editing)</span>}
        </h2>
        {rfqDetail.company_admin_rejection && (
          <span style={{
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            padding: '0.25rem 0.75rem',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            ⚠️ Rejected
          </span>
        )}
        {rfqDetail.control_status === 'rejected' && (
          <span style={{
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            padding: '0.25rem 0.75rem',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            ⚠️ Control Rejected
          </span>
        )}
      </div>
      <div className="info-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1rem'
      }}>
        <div className="info-item">
          <label style={{ fontWeight: '500', color: '#6b7280' }}>Title:</label>
          {isEditingRequest ? (
            <input
              type="text"
              value={requestEditForm.title}
              onChange={(e) => setRequestEditForm({ ...requestEditForm, title: e.target.value })}
              style={{
                display: 'block',
                width: '100%',
                marginTop: '0.25rem',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px'
              }}
            />
          ) : (
            <span style={{ display: 'block', marginTop: '0.25rem' }}>{rfqDetail.title || 'Not specified'}</span>
          )}
        </div>
        <div className="info-item">
          <label style={{ fontWeight: '500', color: '#6b7280' }}>Contact Person:</label>
          {isEditingRequest ? (
            <input
              type="text"
              value={requestEditForm.contact_person}
              onChange={(e) => setRequestEditForm({ ...requestEditForm, contact_person: e.target.value })}
              style={{
                display: 'block',
                width: '100%',
                marginTop: '0.25rem',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px'
              }}
            />
          ) : (
            <span style={{ display: 'block', marginTop: '0.25rem' }}>{rfqDetail.contact_person || 'Not specified'}</span>
          )}
        </div>
        <div className="info-item">
          <label style={{ fontWeight: '500', color: '#6b7280' }}>Created:</label>
          <span style={{ display: 'block', marginTop: '0.25rem' }}>{rfqDetail.created_at ? new Date(rfqDetail.created_at).toLocaleDateString() : 'Unknown'}</span>
        </div>
        <div className="info-item">
          <label style={{ fontWeight: '500', color: '#6b7280' }}>Delivery Address:</label>
          {isEditingRequest ? (
            <select
              value={selectedDeliveryAddressId}
              onChange={(e) => setSelectedDeliveryAddressId(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                marginTop: '0.25rem',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#fff'
              }}
            >
              <option value="">Select address</option>
              {addresses.map((addr) => (
                <option key={addr._id} value={addr._id}>
                  {addr.address_name || `${addr.street}, ${addr.city}`}
                  {addr.is_default && ' (Default)'}
                </option>
              ))}
            </select>
          ) : (
            <span style={{ display: 'block', marginTop: '0.25rem' }}>{rfqDetail.delivery_address || 'Not specified'}</span>
          )}
        </div>
        <div className="info-item">
          <label style={{ fontWeight: '500', color: '#6b7280' }}>Invoice Address:</label>
          {isEditingRequest ? (
            <select
              value={selectedInvoiceAddressId}
              onChange={(e) => setSelectedInvoiceAddressId(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                marginTop: '0.25rem',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#fff'
              }}
            >
              <option value="">Select address</option>
              {addresses.map((addr) => (
                <option key={addr._id} value={addr._id}>
                  {addr.address_name || `${addr.street}, ${addr.city}`}
                  {addr.is_default && ' (Default)'}
                </option>
              ))}
            </select>
          ) : (
            <span style={{ display: 'block', marginTop: '0.25rem' }}>{rfqDetail.invoice_address || 'Not specified'}</span>
          )}
        </div>
      </div>
    </div>
  ) : null;

  // Combined header content with warning + info card
  const headerContentWithWarning = (
    <>
      {manualProductWarningCard}
      {controlPhaseWarningCard}
      {waitingApprovalWarningCard}
      {requestInfoCard}
    </>
  );

  // Separate header content for approval mode (without Link Product header - it will wrap the table)
  const approvalPanelOnly = approveTarget ? (
    <div className="manual-approval-panel">
      <div className="panel-header">
        <div>
          <h3>Manual Product Details</h3>
          <p>Review and adjust the manual entry before approving.</p>
        </div>
        <div className="panel-header-actions">
          {isGlobalMode && approveTarget?.request_id && (
            <button
              onClick={() =>
                onNavigate(
                  `/dashboard/rfqs/open/${typeof approveTarget.request_id === 'string'
                    ? approveTarget.request_id
                    : String(approveTarget.request_id)
                  }`
                )
              }
              className="secondary-button"
            >
              View RFQ
            </button>
          )}
        </div>
      </div>

      <div className="panel-body">
        {isGlobalMode && (
          <div className="manual-request-meta">
            <div>
              <span className="meta-label">Request Title:</span>
              <span className="meta-value">{approveTarget.request_title || '-'}</span>
            </div>
            <div>
              <span className="meta-label">Company:</span>
              <span className="meta-value">
                {approveTarget.company_name || approveTarget.company_id_display || '-'}
              </span>
            </div>
            <div>
              <span className="meta-label">Req #:</span>
              <span className="meta-value">
                {approveTarget.request_company_request_id ?? '-'}
              </span>
            </div>
          </div>
        )}

        <div className="manual-product-form">
          <div className="form-grid">
            <label>
              <span>Product Name <span style={{ color: '#ef4444' }}>*</span></span>
              <input
                type="text"
                value={editForm.product_name}
                onChange={(e) => handleFieldChange('product_name', e.target.value)}
                placeholder="Enter product name"
              />
            </label>
            <label>
              <span>Brand <span style={{ color: '#ef4444' }}>*</span></span>
              <input
                type="text"
                value={editForm.product_brand}
                onChange={(e) => handleFieldChange('product_brand', e.target.value)}
                placeholder="Enter brand name"
              />
            </label>
            {isAdmin && (
              <label>
                <span>Amazon ASIN</span>
                <input
                  type="text"
                  value={editForm.amazon_asin || ''}
                  onChange={(e) => handleFieldChange('amazon_asin', e.target.value)}
                  placeholder="Enter Amazon ASIN"
                />
              </label>
            )}
            <label>
              <span>Categories <span style={{ color: '#ef4444' }}>*</span></span>
              <select
                value={editForm.category || ''}
                onChange={(e) => handleFieldChange('category', e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Select category...</option>
                {CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name.tr}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Base Unit <span style={{ color: '#ef4444' }}>*</span></span>
              <select
                value={editForm.base_unit || 'kg'}
                onChange={(e) => handleFieldChange('base_unit', e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="l">l</option>
                <option value="ml">ml</option>
                <option value="pcs">pcs</option>
                <option value="box">box</option>
                <option value="pack">pack</option>
                <option value="m">m</option>
                <option value="cm">cm</option>
              </select>
            </label>
            <label>
              <span>Size</span>
              <select
                value={editForm.dimensions || ''}
                onChange={(e) => handleFieldChange('dimensions', e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Select Size (Optional)</option>
                <option value="XS">XS</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
              </select>
            </label>
            <label>
              <span>Color</span>
              <input
                type="text"
                value={editForm.color}
                onChange={(e) => handleFieldChange('color', e.target.value)}
                placeholder="Enter color"
              />
            </label>
            <label>
              <span>Description</span>
              <input
                type="text"
                value={editForm.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                placeholder="Enter product description"
              />
            </label>
            {/* Company Code for users - in grid */}
            {!isAdmin && (
              <label>
                <span>Company Code</span>
                <input
                  type="text"
                  value={companyCodes[userCompanyId] || ''}
                  onChange={(e) => setCompanyCodes({
                    ...companyCodes,
                    [userCompanyId]: e.target.value
                  })}
                  placeholder="Enter code (default: 1)"
                />
              </label>
            )}
          </div>

          {/* Company Specific Codes Section - Admin only */}
          {isAdmin && (
            <div style={{ marginTop: '0.5rem' }}>
              <span style={{ fontWeight: '600', color: '#1f2937', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                Company Specific Codes (Optional)
              </span>
              <CompanySelector
                value={selectedCompanies}
                onChange={setSelectedCompanies}
                placeholder="Select companies to add codes..."
                multiple={true}
              />
            </div>
          )}

          {/* Company Codes Inputs - Admin only */}
          {isAdmin && selectedCompanies.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <span style={{ fontWeight: '600', color: '#1f2937', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                Company Codes
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {selectedCompanies.map((company) => (
                  <div key={company.company_id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <label style={{ minWidth: '200px', fontSize: '13px', color: '#4b5563' }}>
                      {company.company_name}:
                    </label>
                    <input
                      type="text"
                      value={companyCodes[company.company_id] || ''}
                      onChange={(e) => setCompanyCodes({
                        ...companyCodes,
                        [company.company_id]: e.target.value
                      })}
                      style={{
                        flex: 1,
                        maxWidth: '200px',
                        padding: '0.5rem 0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '0.95rem'
                      }}
                      placeholder="Enter code (default: 1)"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              className="primary-button"
              onClick={handleSaveManualChanges}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  if (approveTarget) {
    return (
      <>
        <DataDashboard
          key={`manual-approval-${approveTarget._id}`}
          title="Link Product"
          collection="products"
          data={similarDashboardData}
          columns={similarDashboardColumns}
          isLoading={similarLoading}
          error={similarError}
          user={user}
          currentRoute={currentRoute}
          onNavigate={handleBlockedNavigate}
          onLogout={onLogout}
          onDataChange={() => { }}
          showAddButton={false}
          showDeleteButton={false}
          showEditButton={false}
          customHeaderActions={approvalHeaderActions}
          navigationActions={approvalNavigationActions}
          onSelectionChange={handleSimilarSelectionChange}
          onDataFetch={handleSimilarDataFetch}
          itemsPerPage={similarFetchParams.limit ?? defaultSimilarLimit}
          paginationInfo={{
            page: 1,
            limit: similarFetchParams.limit ?? defaultSimilarLimit,
            total: similarProducts.length,
            pages: similarProducts.length > 0 ? 1 : 0,
            has_next: false,
            has_prev: false
          }}
          pageDescription="Select an existing product to link, or add to master data"
          layoutType="user"
          getDynamicSubItems={getDynamicSubItems}
          headerContent={approvalHeaderContent}
          className="rfq-manual-products-dashboard approval-mode link-product-card"
        />
      </>
    );
  }

  return (
    <>
      <DataDashboard
        key={dashboardKey}
        title="RFQ Review"
        collection="request_products"
        data={manualProducts}
        columns={activeColumns}
        isLoading={isLoading}
        error={error}
        user={user}
        currentRoute={currentRoute}
        onNavigate={handleBlockedNavigate}
        onLogout={onLogout}
        onDataChange={setManualProducts}
        showAddButton={false}
        showDeleteButton={false}
        showEditButton={false}
        customHeaderActions={headerActions}
        navigationActions={(() => {
          const navInfo = getRFQNavigationInfo(currentRoute);
          if (!navInfo) return undefined;

          // Check for rfq_operations permission
          const permissions = user?.permissions;
          const hasRfqOperations =
            (Array.isArray(permissions) ? permissions.includes('rfq_operations') : permissions?.rfq_operations) ||
            user?.role === 'admin' ||
            (user as any)?.is_admin;

          if (!hasRfqOperations) {
            return undefined;
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {navInfo.previousPage ? (
                  <button
                    onClick={() => handleBlockedNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.previousPage!))}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      backgroundColor: '#ffffff',
                      color: '#374151',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    ← Back to {getRFQPageName(navInfo.previousPage)}
                  </button>
                ) : (
                  <button
                    onClick={() => handleBlockedNavigate('/dashboard/user/inbox/open')}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      backgroundColor: '#ffffff',
                      color: '#374151',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    ← Back to Open RFQs
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {navInfo.nextPage && (navInfo.nextPage !== 'products' || isAdmin) && (
                  <button
                    onClick={() => handleBlockedNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.nextPage!))}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      backgroundColor: '#ffffff',
                      color: '#374151',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    Next to {getRFQPageName(navInfo.nextPage)} →
                  </button>
                )}
              </div>
            </div>
          );
        })()}
        onSelectionChange={handleSelectionChange}
        pageDescription={
          isGlobalMode
            ? 'All manual products across requests awaiting review'
            : 'Detailed view of all products in this RFQ'
        }
        layoutType="user"
        getDynamicSubItems={getDynamicSubItems}
        className="rfq-manual-products-dashboard"
        headerContent={headerContentWithWarning}
      />
      <ConfirmationModal
        isOpen={isSplitModalOpen}
        onClose={() => setIsSplitModalOpen(false)}
        onConfirm={executeSplitRFQ}
        title="Split RFQ"
        message={`Are you sure you want to split ${selectedIds.length} product(s) into a new RFQ?`}
        confirmText="Split"
        cancelText="Cancel"
        type="info"
      />
      <ConfirmationModal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        onConfirm={handleConfirmApprove}
        title="Approve Request"
        message="Are you sure you want to approve this request? Once approved, it will move to the control phase."
        confirmText={isApproving ? "Approving..." : "Approve"}
        cancelText="Cancel"
        type="info"
      />
      <ConfirmationModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={executeCancelRequest}
        title="Finalize (Cancel) Request"
        message="Are you sure you want to finalize this request as CANCELLED? This action cannot be undone."
        confirmText={isCancelling ? "Cancelling..." : "Confirm Cancel"}
        cancelText="Keep Request"
        type="info"
      />
      <SuccessModal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        message={successMessage}
      />
      <ConfirmationModal
        isOpen={isNavigationBlockModalOpen}
        onClose={handleCancelNavigation}
        onConfirm={hasManualProducts || (rfqDetail && rfqDetail.company_admin_approval === false) ? handleCancelNavigation : () => {
          handleConfirmControl();
          setIsNavigationBlockModalOpen(false);
        }}
        title={
          hasManualProducts
            ? "Manual Products Pending"
            : rfqDetail && rfqDetail.company_admin_approval === false
              ? "Request Awaiting Approval"
              : "Control Phase: Confirmation Required"
        }
        message={
          hasManualProducts
            ? `You have ${manualProductCount} manual product(s) that need approval. Please approve all manual products before leaving this page.`
            : rfqDetail && rfqDetail.company_admin_approval === false
              ? "This request is pending company admin approval. You cannot proceed to other pages until it is approved."
              : "This request is in the Control Phase. Please review the details and click 'Confirm Request' to finalize before proceeding."
        }
        confirmText={hasManualProducts || (rfqDetail && rfqDetail.company_admin_approval === false) ? "Okay" : "Confirm Request"}
        cancelText="Cancel"
        type="info"
        hideCancel={hasManualProducts || (rfqDetail && rfqDetail.company_admin_approval === false)}
      />

      {/* Product Edit Modal */}
      {showProductEditModal && editingProduct && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>
                {editingProduct.is_manual ? 'Edit Product' : 'Edit Quantity'}
              </h3>
              <button
                onClick={() => { setShowProductEditModal(false); setEditingProduct(null); }}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Product Name - only editable for manual products */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Product Name</label>
                <input
                  type="text"
                  value={productEditForm.product_name}
                  onChange={(e) => setProductEditForm({ ...productEditForm, product_name: e.target.value })}
                  disabled={!editingProduct.is_manual}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: editingProduct.is_manual ? 'white' : '#f3f4f6'
                  }}
                />
              </div>

              {/* Brand - only editable for manual products */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Brand</label>
                <input
                  type="text"
                  value={productEditForm.product_brand}
                  onChange={(e) => setProductEditForm({ ...productEditForm, product_brand: e.target.value })}
                  disabled={!editingProduct.is_manual}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: editingProduct.is_manual ? 'white' : '#f3f4f6'
                  }}
                />
              </div>

              {/* Quantity - always editable */}
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Quantity *</label>
                  <input
                    type="number"
                    value={productEditForm.quantity}
                    onChange={(e) => setProductEditForm({ ...productEditForm, quantity: parseInt(e.target.value) || 1 })}
                    min="1"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #3b82f6',
                      borderRadius: '6px',
                      backgroundColor: 'white'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Unit *</label>
                  <input
                    type="text"
                    value={productEditForm.unit}
                    onChange={(e) => setProductEditForm({ ...productEditForm, unit: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #3b82f6',
                      borderRadius: '6px',
                      backgroundColor: 'white'
                    }}
                  />
                </div>
              </div>

              {/* Category - only editable for manual products */}
              {editingProduct.is_manual && (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Category</label>
                  <input
                    type="text"
                    value={productEditForm.category}
                    onChange={(e) => setProductEditForm({ ...productEditForm, category: e.target.value })}
                    placeholder="e.g. Electronics, Office"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px'
                    }}
                  />
                </div>
              )}

              {/* Budget - only editable for manual products and non-basic plan */}
              {editingProduct.is_manual && !isBasicPlan && (
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Budget</label>
                    <input
                      type="number"
                      value={productEditForm.budget}
                      onChange={(e) => setProductEditForm({ ...productEditForm, budget: parseFloat(e.target.value) || 0 })}
                      min="0"
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Currency</label>
                    <select
                      value={productEditForm.currency}
                      onChange={(e) => setProductEditForm({ ...productEditForm, currency: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px'
                      }}
                    >
                      <option value="TRY">TRY</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button
                onClick={() => { setShowProductEditModal(false); setEditingProduct(null); }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProductEdit}
                disabled={isUpdatingProduct}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  opacity: isUpdatingProduct ? 0.7 : 1
                }}
              >
                {isUpdatingProduct ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <RejectRequestModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={handleRejectConfirm}
        requestId={requestId || undefined}
        requestName={rfqDetail?.title || rfqDetail?.company_request_id}
        isRejecting={isRejecting}
      />

      {/* Control Phase Reject Modal */}
      <RejectRequestModal
        isOpen={showRejectControlModal}
        onClose={() => setShowRejectControlModal(false)}
        onConfirm={handleRejectControl}
        requestId={requestId || undefined}
        requestName={rfqDetail?.title || rfqDetail?.company_request_id}
        isRejecting={isRejectingControl}
      />

      <RfqLockedModal
        isOpen={showLockModal}
        lockInfo={lockInfo}
        onClose={() => setShowLockModal(false)}
        onGoBack={() => {
          setShowLockModal(false);
          onNavigate('/dashboard/user/inbox');
        }}
      />
    </>
  );
};

export default UserRFQManualProductsDashboard;

